"use client";
import React, { useEffect } from "react";
import { AlertTriangle } from "lucide-react";

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    /**
     * Les libellés des deux boutons.
     *
     * « Annuler / Continuer » sous un titre « Quitter ? » est ambigu : on peut
     * lire « continuer à modifier » aussi bien que « continuer à quitter ».
     * Un bouton doit dire ce qu'il FAIT, pas où l'on en est.
     */
    confirmText?: string;
    cancelText?: string;
}

export function ConfirmationModal({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }: ConfirmationModalProps) {
    // Handle Escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) {
            window.addEventListener("keydown", handleEsc);
        }
        return () => window.removeEventListener("keydown", handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative w-full max-w-md glass-card p-6 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 border border-border dark:border-white/10 bg-background dark:bg-[#0A0A0A]/90">
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-orange-500/10 text-orange-500">
                        <AlertTriangle className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            {message}
                        </p>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
                    >
                        {cancelText ?? "Annuler"}
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                    >
                        {confirmText ?? "Continuer"}
                    </button>
                </div>
            </div>
        </div>
    );
}
